import { api } from "@/lib/apiClient";
import { computeHoursFromTimeRange } from "@/lib/employeeTimes";

export interface WorkTimeModelDay {
  id?: number;
  weekday: number;
  is_workday: boolean;
  time_from: string | null;
  time_to: string | null;
  break_from: string | null;
  break_to: string | null;
  hours_decimal: number;
}

export interface WorkTimeModel {
  id: number;
  name: string;
  is_default: boolean;
  days: WorkTimeModelDay[];
  weekly_hours: number;
  assigned_count: number;
  created_at: string;
  updated_at: string;
}

export interface WorkTimeModelAssignment {
  user_login: string;
  user_display: string | null;
  model_id: number;
  model_name: string;
  is_default_model: boolean;
  effective_from: string;
  assigned_at: string | null;
  assigned_by_login: string | null;
}

export const WEEKDAY_LABELS: Record<number, string> = {
  1: "Montag",
  2: "Dienstag",
  3: "Mittwoch",
  4: "Donnerstag",
  5: "Freitag",
  6: "Samstag",
  7: "Sonntag",
};

export const WEEKDAY_SHORT: Record<number, string> = {
  1: "Mo",
  2: "Di",
  3: "Mi",
  4: "Do",
  5: "Fr",
  6: "Sa",
  7: "So",
};

export function computeNetHoursFromRanges(
  timeFrom: string,
  timeTo: string,
  breakFrom?: string | null,
  breakTo?: string | null,
): number {
  let hours = computeHoursFromTimeRange(timeFrom, timeTo);
  if (breakFrom && breakTo) {
    hours -= computeHoursFromTimeRange(breakFrom, breakTo);
  }
  return Math.max(0, Math.round(hours * 100) / 100);
}

export function createDefaultModelDays(): WorkTimeModelDay[] {
  return Array.from({ length: 7 }, (_, idx) => {
    const weekday = idx + 1;
    const isWorkday = weekday <= 5;
    return {
      weekday,
      is_workday: isWorkday,
      time_from: isWorkday ? "08:00" : null,
      time_to: isWorkday ? "16:30" : null,
      break_from: isWorkday ? "12:00" : null,
      break_to: isWorkday ? "12:30" : null,
      hours_decimal: isWorkday ? 8 : 0,
    };
  });
}

export function normalizeModelDay(day: WorkTimeModelDay): WorkTimeModelDay {
  if (!day.is_workday) {
    return {
      ...day,
      time_from: null,
      time_to: null,
      break_from: null,
      break_to: null,
      hours_decimal: 0,
    };
  }
  const from = day.time_from?.slice(0, 5) || "08:00";
  const to = day.time_to?.slice(0, 5) || "16:30";
  const breakFrom = day.break_from?.slice(0, 5) || null;
  const breakTo = day.break_to?.slice(0, 5) || null;
  return {
    ...day,
    time_from: from,
    time_to: to,
    break_from: breakFrom,
    break_to: breakTo,
    hours_decimal: computeNetHoursFromRanges(from, to, breakFrom, breakTo),
  };
}

export function listWorkTimeModels(): Promise<WorkTimeModel[]> {
  return api.get("/employee-times/work-models");
}

export function getWorkTimeModel(id: number): Promise<WorkTimeModel> {
  return api.get(`/employee-times/work-models/${id}`);
}

export function createWorkTimeModel(payload: {
  name: string;
  days: WorkTimeModelDay[];
}): Promise<WorkTimeModel> {
  return api.post("/employee-times/work-models", {
    name: payload.name,
    days: payload.days.map(normalizeModelDay),
  });
}

export function updateWorkTimeModel(
  id: number,
  payload: { name?: string; days?: WorkTimeModelDay[] },
): Promise<WorkTimeModel> {
  return api.put(`/employee-times/work-models/${id}`, {
    name: payload.name,
    days: payload.days?.map(normalizeModelDay),
  });
}

export function deleteWorkTimeModel(id: number): Promise<{ status: string; id: number }> {
  return api.del(`/employee-times/work-models/${id}`);
}

function buildQuery(params: Record<string, string | undefined>): string {
  const qs = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value != null && value !== "") qs.set(key, value);
  }
  const s = qs.toString();
  return s ? `?${s}` : "";
}

export function listWorkTimeAssignments(params?: {
  user_login?: string;
}): Promise<WorkTimeModelAssignment[]> {
  return api.get(
    `/employee-times/work-models/assignments${buildQuery({
      user_login: params?.user_login,
    })}`,
  );
}

export function assignWorkTimeModel(
  userLogin: string,
  payload: { model_id: number; user_display?: string; effective_from?: string },
): Promise<WorkTimeModelAssignment> {
  return api.put(`/employee-times/work-models/assignments/${encodeURIComponent(userLogin)}`, payload);
}

export function removeWorkTimeAssignment(
  userLogin: string,
  effectiveFrom?: string,
): Promise<{ status: string; user_login: string; effective_from?: string | null }> {
  return api.del(
    `/employee-times/work-models/assignments/${encodeURIComponent(userLogin)}${buildQuery({
      effective_from: effectiveFrom,
    })}`,
  );
}
